
import {
  defineComponent,
  ref,
  computed,
  PropType,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";
import FlowDiagram from "@/flow/components/vue-flow/FlowDiagram.vue";
import FlowPreviewCustomFields from "@/flow/components/wizard/FlowPreviewCustomFields.vue";
import {
  buildWizardDraftFlowSnapshot,
  countWizardDraftSteps,
} from "@/flow/utils/wizardDraftFlow";

type ResizeObserverLike = {
  disconnect: () => void;
  observe: (target: Element) => void;
};

type ResizeObserverConstructorLike = new (
  callback: (...args: any[]) => void
) => ResizeObserverLike;

export default defineComponent({
  name: "FlowPreviewOverlay",
  components: { FlowDiagram, FlowPreviewCustomFields },
  props: {
    flowSpec: {
      type: Object as PropType<any>,
      required: true,
    },
    approvalIntent: {
      type: String as PropType<"create" | "update">,
      default: "create",
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["approve", "reject", "feedback"],
  setup(props, { emit }) {
    const showFeedbackInput = ref(false);
    const feedbackText = ref("");
    const overlayRef = ref<HTMLElement | null>(null);
    const actionsRowRef = ref<HTMLElement | null>(null);
    const diagramRef = ref<{ locateFlow?: () => void } | null>(null);
    const actionsPanelWidth = ref<number | null>(null);
    let overlayResizeObserver: ResizeObserverLike | null = null;
    let widthSyncTimeoutId: number | null = null;

    const draftFlow = computed(() =>
      buildWizardDraftFlowSnapshot(props.flowSpec)
    );
    const draftSteps = computed(() => draftFlow.value?.steps || []);
    const flowName = computed(() => draftFlow.value?.name || "Untitled Flow");
    // Counts branch steps too — draftSteps holds only the top level, so a router would
    // otherwise be reported as one step no matter how much work sits inside its branches.
    const stepCount = computed(() => countWizardDraftSteps(props.flowSpec));
    // `flowSpec` is declared `PropType<any>`, but defineComponent's inference resolves it to
    // `unknown` here, so reaching INTO it fails the type check even though passing it whole to an
    // `any` parameter (every other use in this file) does not. Narrow it once, explicitly.
    const approvalChanges = computed<any[]>(() => {
      const spec = props.flowSpec as any;
      return Array.isArray(spec?.approval_changes) ? spec.approval_changes : [];
    });
    const actionsPanelStyle = computed(() =>
      actionsPanelWidth.value ? { width: `${actionsPanelWidth.value}px` } : {}
    );
    const submitLabel = computed(() =>
      props.approvalIntent === "update" ? "Update Flow" : "Create Flow"
    );
    const submittingLabel = computed(() =>
      props.approvalIntent === "update"
        ? "Updating Flow..."
        : "Creating Flow..."
    );

    async function updateActionsPanelWidth() {
      await nextTick();

      if (
        !actionsRowRef.value ||
        !overlayRef.value ||
        typeof window === "undefined"
      ) {
        return;
      }

      const actionButtons = Array.from(
        actionsRowRef.value.children
      ) as HTMLElement[];

      if (actionButtons.length === 0) {
        actionsPanelWidth.value = null;
        return;
      }

      const horizontalPadding = 40;
      const buttonGap = 10;
      const overlayPadding = 48;
      const contentWidth =
        actionButtons.reduce(
          (totalWidth, button) =>
            totalWidth + Math.ceil(button.getBoundingClientRect().width),
          0
        ) +
        buttonGap * Math.max(actionButtons.length - 1, 0);
      const overlayWidth = Math.max(
        Math.floor(overlayRef.value.getBoundingClientRect().width) -
          overlayPadding,
        0
      );
      const viewportWidth = Math.floor(window.innerWidth * 0.9);

      actionsPanelWidth.value = Math.min(
        contentWidth + horizontalPadding,
        overlayWidth,
        viewportWidth
      );
    }

    async function refitPreviewDiagram() {
      await nextTick();
      const locateFlow = diagramRef.value?.locateFlow;
      if (locateFlow) {
        locateFlow();
      }
    }

    function schedulePreviewLayoutUpdate() {
      updateActionsPanelWidth();
      refitPreviewDiagram();

      if (typeof window === "undefined") {
        return;
      }

      if (widthSyncTimeoutId !== null) {
        window.clearTimeout(widthSyncTimeoutId);
      }

      // Re-measure after the drawer transition settles on direct-open paths.
      widthSyncTimeoutId = window.setTimeout(() => {
        widthSyncTimeoutId = null;
        updateActionsPanelWidth();
        refitPreviewDiagram();
      }, 240);
    }

    function getResizeObserverConstructor(): ResizeObserverConstructorLike | null {
      if (typeof window === "undefined") {
        return null;
      }

      return (
        (
          window as Window & {
            ResizeObserver?: ResizeObserverConstructorLike;
          }
        ).ResizeObserver || null
      );
    }

    function sendFeedback() {
      const text = feedbackText.value.trim();
      if (text) {
        emit("feedback", text);
        feedbackText.value = "";
        showFeedbackInput.value = false;
      }
    }

    onMounted(() => {
      schedulePreviewLayoutUpdate();

      const ResizeObserverConstructor = getResizeObserverConstructor();

      if (ResizeObserverConstructor && overlayRef.value) {
        overlayResizeObserver = new ResizeObserverConstructor(() => {
          schedulePreviewLayoutUpdate();
        });
        overlayResizeObserver.observe(overlayRef.value);
      }

      if (typeof window !== "undefined") {
        window.addEventListener("resize", schedulePreviewLayoutUpdate);
      }
    });

    onBeforeUnmount(() => {
      if (overlayResizeObserver) {
        overlayResizeObserver.disconnect();
        overlayResizeObserver = null;
      }

      if (widthSyncTimeoutId !== null && typeof window !== "undefined") {
        window.clearTimeout(widthSyncTimeoutId);
        widthSyncTimeoutId = null;
      }

      if (typeof window !== "undefined") {
        window.removeEventListener("resize", schedulePreviewLayoutUpdate);
      }
    });

    watch(
      () => showFeedbackInput.value,
      (isFeedbackVisible) => {
        if (!isFeedbackVisible) {
          schedulePreviewLayoutUpdate();
        }
      }
    );

    watch(
      () => props.submitting,
      () => {
        schedulePreviewLayoutUpdate();
      }
    );

    watch(
      () => props.flowSpec,
      () => {
        schedulePreviewLayoutUpdate();
      },
      { deep: true }
    );

    return {
      showFeedbackInput,
      feedbackText,
      overlayRef,
      actionsRowRef,
      diagramRef,
      actionsPanelStyle,
      flowName,
      stepCount,
      approvalChanges,
      draftFlow,
      draftSteps,
      submitLabel,
      submittingLabel,
      sendFeedback,
    };
  },
});
